import * as React from 'react';
import type { ColumnDef } from '@tanstack/react-table';
import { useNavigate } from 'react-router-dom';
import { Ban, RotateCcw } from 'lucide-react';
import { PageHeader } from '@/components/app/PageHeader';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Sparkline } from '@/components/app/Sparkline';
import { DataTable } from '@/components/app/DataTable';
import { humanizeError } from '@/lib/apiClient';
import { useConfirmDialog } from '@/hooks/useConfirmDialog';
import { ErrorState } from '@/components/app/ErrorState';
import { useDashboardTimeseries, useJourneys, useKillJourney, useReviveJourney } from '../hooks';
import { JourneyStatusBadge } from '../components/JourneyStatusBadge';
import type { JourneyDefinition, NotificationDashboardSnapshot } from '../types';

interface JourneyMetrics {
  sendsByDay: number[];
  totalSends: number;
  totalOpens: number;
  openRate: number | null;
}

function buildMetricsMap(
  snapshots: NotificationDashboardSnapshot[] | undefined,
): Map<string, JourneyMetrics> {
  const result = new Map<string, JourneyMetrics>();
  if (!snapshots || snapshots.length === 0) return result;

  const ids = new Set<string>();
  for (const snap of snapshots) for (const id of Object.keys(snap.byJourney)) ids.add(id);

  for (const journeyId of ids) {
    const sendsByDay: number[] = [];
    let totalSends = 0;
    let totalOpens = 0;
    for (const snap of snapshots) {
      const entry = snap.byJourney[journeyId];
      const sends = entry?.sends ?? 0;
      sendsByDay.push(sends);
      totalSends += sends;
      totalOpens += entry?.opens ?? 0;
    }
    const openRate = totalSends > 0 ? totalOpens / totalSends : null;
    result.set(journeyId, { sendsByDay, totalSends, totalOpens, openRate });
  }
  return result;
}

const EMPTY_METRICS: JourneyMetrics = {
  sendsByDay: [],
  totalSends: 0,
  totalOpens: 0,
  openRate: null,
};

function openRateVariant(rate: number | null): 'success' | 'warning' | 'destructive' | 'outline' {
  if (rate === null) return 'outline';
  if (rate >= 0.3) return 'success';
  if (rate >= 0.1) return 'warning';
  return 'destructive';
}

function formatRate(rate: number | null): string {
  if (rate === null) return '—';
  return `${Math.round(rate * 100)}%`;
}

export function JourneysPage() {
  const { data, isLoading, isError, error, refetch } = useJourneys();
  const timeseries = useDashboardTimeseries(14);
  const killMut = useKillJourney();
  const reviveMut = useReviveJourney();
  const confirm = useConfirmDialog();
  const navigate = useNavigate();

  const snapshots = timeseries.data?.snapshots;
  const metricsMap = React.useMemo(() => buildMetricsMap(snapshots), [snapshots]);

  const openKill = (j: JourneyDefinition) => {
    confirm({
      title: `Kill journey "${j.name}"?`,
      description:
        'In-flight journey steps will stop at the next orchestrator tick. Type the journey ID to confirm.',
      typedConfirmation: j.journeyId,
      destructive: true,
      confirmLabel: 'Kill journey',
      reasonPlaceholder: `e.g. "Too aggressive for cohort X — ${new Date().toISOString().slice(0, 10)}"`,
      onConfirm: async ({ reason }) => {
        await killMut.mutateAsync({ journeyId: j.journeyId, reason });
      },
    });
  };

  const openRevive = (j: JourneyDefinition) => {
    confirm({
      title: `Revive journey "${j.name}"?`,
      description: 'Steps will resume at the next orchestrator tick.',
      confirmLabel: 'Revive',
      reasonPlaceholder: 'Reason (optional, but written to audit)',
      reasonRequired: false,
      onConfirm: async ({ reason }) => {
        await reviveMut.mutateAsync({ journeyId: j.journeyId, reason: reason || undefined });
      },
    });
  };

  const columns = React.useMemo<ColumnDef<JourneyDefinition>[]>(
    () => [
      {
        accessorKey: 'name',
        header: 'Name',
        cell: ({ row }) => (
          <div className="min-w-0">
            <div className="truncate font-medium">{row.original.name}</div>
            <div className="truncate font-mono text-[10px] text-muted-foreground">
              {row.original.journeyId}
            </div>
          </div>
        ),
      },
      {
        id: 'status',
        header: 'Status',
        cell: ({ row }) => <JourneyStatusBadge journey={row.original} />,
      },
      {
        id: 'steps',
        header: 'Steps',
        cell: ({ row }) => (
          <span className="tabular-nums text-xs">{row.original.steps?.length ?? 0}</span>
        ),
      },
      {
        id: 'volume',
        header: '14d sends',
        cell: ({ row }) => {
          const m = metricsMap.get(row.original.journeyId) ?? EMPTY_METRICS;
          return (
            <div className="flex items-center gap-2">
              <Sparkline values={m.sendsByDay} ariaLabel={`14-day sends for ${row.original.name}`} />
              <span className="tabular-nums text-xs text-muted-foreground">
                {m.totalSends.toLocaleString()}
              </span>
            </div>
          );
        },
      },
      {
        id: 'openRate',
        header: 'Open rate',
        cell: ({ row }) => {
          const m = metricsMap.get(row.original.journeyId) ?? EMPTY_METRICS;
          return (
            <Badge variant={openRateVariant(m.openRate)}>{formatRate(m.openRate)}</Badge>
          );
        },
      },
      {
        id: 'updated',
        header: 'Updated',
        cell: ({ row }) => (
          <span className="text-xs text-muted-foreground" title={row.original.updatedAt}>
            {formatRelative(row.original.updatedAt)}
          </span>
        ),
      },
      {
        id: 'actions',
        header: '',
        cell: ({ row }) => (
          <div
            className="flex items-center justify-end gap-2"
            role="presentation"
            onClick={(e) => e.stopPropagation()}
            onKeyDown={(e) => e.stopPropagation()}
          >
            {row.original.isActive ? (
              <Button size="sm" variant="destructive" onClick={() => openKill(row.original)}>
                <Ban className="h-3.5 w-3.5" /> Kill
              </Button>
            ) : (
              <Button size="sm" variant="outline" onClick={() => openRevive(row.original)}>
                <RotateCcw className="h-3.5 w-3.5" /> Revive
              </Button>
            )}
          </div>
        ),
      },
    ],
    // openKill/openRevive close over hook state but are stable per render.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [metricsMap],
  );

  return (
    <div className="space-y-6">
      <PageHeader
        title="Notifications · Journeys"
        description="Multi-step notification sequences. 14-day sends, open rate, and current enrollment at a glance."
      />

      {isError && (
        <ErrorState title="Journeys unavailable" description={humanizeError(error)} onAction={() => refetch()} actionLabel="Retry" />
      )}

      <Card>
        <CardHeader>
          <CardTitle>All journeys</CardTitle>
          <CardDescription>
            Click a row for timeline, trend chart, and recent decisions. Metrics pull from the last
            14 daily snapshots.
          </CardDescription>
        </CardHeader>
        <CardContent>
          <DataTable<JourneyDefinition>
            columns={columns}
            data={data?.journeys ?? []}
            loading={isLoading}
            rowKey={(r) => r.journeyId}
            emptyLabel="No journeys defined"
            emptyDescription="Define a journey via seedJourneyDefinitions."
            onRowClick={(row) => navigate(`/notifications/journeys/${encodeURIComponent(row.journeyId)}`)}
          />
        </CardContent>
      </Card>
    </div>
  );
}

function formatRelative(iso?: string) {
  if (!iso) return '—';
  const ms = Date.now() - new Date(iso).getTime();
  if (!Number.isFinite(ms)) return iso;
  const s = Math.max(0, Math.floor(ms / 1000));
  if (s < 60) return `${s}s ago`;
  const m = Math.floor(s / 60);
  if (m < 60) return `${m}m ago`;
  const h = Math.floor(m / 60);
  if (h < 24) return `${h}h ago`;
  return `${Math.floor(h / 24)}d ago`;
}
